import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
    useEffect
} from 'react'
import { useRouter } from 'next/router'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import type {
    ProductAttributeSortInput,
    SortFields
} from '@pylot-data/fwrdschema'
import { SortEnum } from '@pylot-data/enums/SortEnum'
import s from './Sort.module.scss'
import { Cross } from '@components/icons'
import { Button, Input } from '@corratech/pylot-ui'
import { SortField } from '@pylot-data/pylotschema'

type CustomSortOptionProps = {
    value: string
    label?: string
    direction?: SortEnum
}

type SortProps = {
    isSortVisible: boolean
    setIsSortVisible: Dispatch<SetStateAction<boolean>>
    activeLabel: string
    setActiveLabel: Dispatch<SetStateAction<string>>
    sortFields: SortFields
    applySort: Dispatch<SetStateAction<ProductAttributeSortInput>>
    appliedSort?: ProductAttributeSortInput | Record<string, never>
    customSortOptions?: Array<CustomSortOptionProps>
}

export const Sort = (props: SortProps): JSX.Element => {
    const { t } = useTranslation('')
    const router = useRouter()

    const {
        isSortVisible,
        setIsSortVisible,
        sortFields: { default: defaultSort, options },
        applySort,
        appliedSort,
        activeLabel,
        setActiveLabel,
        customSortOptions
    } = props

    useEffect(() => {
        if (isSortVisible) {
            const scrollY = window.scrollY
            document.body.style.position = 'fixed'
            document.body.style.top = `-${scrollY}px`
            document.body.style.left = '0'
            document.body.style.right = '0'
        }

        return () => {
            // Este bloque se ejecuta al desmontar el componente también
            const scrollY = document.body.style.top
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.left = ''
            document.body.style.right = ''
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1)
            }
        }
    }, [isSortVisible])

    const activeSortKey =
        appliedSort && Object.entries(appliedSort).length
            ? Object.entries(appliedSort)[0].join('')
            : ''

    const isSearchPage = router.pathname === '/search'

    const getSortItem = useCallback(
        (value: string, label: string, direction: SortEnum = SortEnum.Asc) => {
            const itemClass = value.includes('price') ? `price` : value
            const itemImg = value.includes('price')
                ? '/icons/price.svg'
                : '/icons/placeholder.svg'

            return (
                <li
                    key={`${value}${direction}`}
                    className={cn(
                        s['sort-item'],
                        activeLabel === label && [s.active]
                    )}
                >
                    <Button
                        className={s['sort-item-label']}
                        variant="link"
                        onClick={() => {
                            applySort({ [value]: direction ?? SortEnum.Asc })
                            setActiveLabel(label)
                            setIsSortVisible(false)
                        }}
                    >
                        <span className={s.label}>{t(label)}</span>
                    </Button>
                </li>
            )
        },
        [activeLabel, applySort, setActiveLabel, setIsSortVisible, t]
    )

    const buildSortOptions = (item: SortField) => {
        if (customSortOptions && customSortOptions.length > 0) {
            const sortOptions = customSortOptions.filter(
                (customSortOption) => customSortOption.value === item.value
            )
            if (sortOptions.length === 0) {
                return getSortItem(item.value!, String(item.label))
            } else {
                return sortOptions.map((sortOption) => {
                    return getSortItem(
                        sortOption.value,
                        sortOption.label
                            ? String(sortOption.label)
                            : String(item.label),
                        sortOption.direction
                            ? sortOption.direction
                            : SortEnum.Asc
                    )
                })
            }
        } else {
            if (item.value == 'saving') {
                return getSortItem(
                    item.value!,
                    String(item.label),
                    SortEnum.Desc
                )
            }
            if (item.value == 'rating_summary') {
                return getSortItem(
                    item.value!,
                    String(item.label),
                    SortEnum.Desc
                )
            }
            return getSortItem(item.value!, String(item.label))
        }
    }

    const sortOptionItems = useMemo(
        () =>
            options &&
            options.map((item) => {
                const hasPositionInSearchPage =
                    isSearchPage && item && item.value === 'position'

                if (!item || !item.value || hasPositionInSearchPage) {
                    return null
                }

                return item.value !== 'price'
                    ? buildSortOptions(item)
                    : React.Children.toArray([
                          getSortItem(
                              item.value,
                              t('toolbar|Price Low'),
                              SortEnum.Asc
                          ),
                          getSortItem(
                              item.value,
                              t('toolbar|Price High'),
                              SortEnum.Desc
                          )
                      ])
            }),
        [activeSortKey, defaultSort, getSortItem, options, t]
    )

    return (
        <div className={s['sort']}>
            <div
                className={s['sort-overlay']}
                onClick={() => setIsSortVisible(!isSortVisible)}
                aria-hidden="true"
            />
            <div
                className={cn(
                    s['sort-content'],
                    isSearchPage && s['sort-content-srp'],
                    'animate-fade-in-right md:animate-fade-in-down'
                )}
            >
                <div className={s['header-sort']}>
                    <h4>{t('SORT BY')}</h4>
                    <Button
                        onClick={() => setIsSortVisible(!isSortVisible)}
                        variant="link"
                        className={cn(s['cart-btn-close'], 'close-btn')}
                        aria-label={t('Close Panel')}
                    >
                        <span aria-hidden="true">
                            <Cross width={22} height={22} />
                        </span>
                    </Button>
                    <p className={s['line-header']} />
                </div>
                <ul className={s['sort-options']}>{sortOptionItems}</ul>
            </div>
        </div>
    )
}
